import { Space } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import BarrierCreateForm from '@/components/barriers/BarrierCreateForm';
import BarrierPropertiesPanel from '@/components/barriers/BarrierPropertiesPanel';
import RouteCreateForm from '@/components/routes/RouteCreateForm';
import RoutePropertiesPanel from '@/components/routes/RoutePropertiesPanel';
import { useBarrierWorkspace } from '@/stores/BarrierWorkspaceContext';
import { useRouteWorkspace } from '@/stores/RouteWorkspaceContext';
import { PANEL_MODE_PATH, writeLastPanelMode, type PanelMode } from '@/utils/panelMode';

import WorkspaceBarriersLibrary from './WorkspaceBarriersLibrary';
import WorkspaceModalHeader from './WorkspaceModalHeader';
import WorkspaceRoutesLibrary from './WorkspaceRoutesLibrary';
import WorkspaceSegmented from './WorkspaceSegmented';

interface WorkspacePanelProps {
  readonly mode: PanelMode;
}

/**
 * Unified right-hand panel shared by `/routes` and `/barriers`. Owns
 * the three orthogonal display states:
 *
 * 1. **Draft** (create-barrier / create-route): full-height form with
 *    a back-chevron; segmented switcher hidden so the user can't
 *    accidentally abandon in-progress work.
 * 2. **Selected** (barrier or route focused): segmented switcher
 *    visible above the property sheet; back-chevron suppresses the
 *    sheet while leaving the selection intact (row in the library
 *    stays highlighted, clicking it reopens the sheet).
 * 3. **Library**: segmented switcher + filters + table for the mode
 *    driven by the URL.
 *
 * Switching segments writes the last-mode preference and clears any
 * lingering selection so the other library lands in a clean state.
 */
export default function WorkspacePanel({ mode }: WorkspacePanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    draft: barrierDraft,
    selectedId: selectedBarrierId,
    selectBarrier,
    cancelDraft: cancelBarrierDraft,
  } = useBarrierWorkspace();
  const {
    draft: routeDraft,
    selectedRouteId,
    selectRoute,
    cancelDraft: cancelRouteDraft,
  } = useRouteWorkspace();

  // Suppression tracks "user dismissed the property sheet for THIS id".
  // A different selection implicitly resets it via the effect below;
  // re-clicking the same row forces a reset via the wrapped handlers.
  const [suppressedRouteId, setSuppressedRouteId] = useState<string | null>(null);
  const [suppressedBarrierId, setSuppressedBarrierId] = useState<number | null>(null);

  useEffect(() => {
    setSuppressedRouteId(null);
  }, [selectedRouteId]);
  useEffect(() => {
    setSuppressedBarrierId(null);
  }, [selectedBarrierId]);

  const handleSegmentChange = useCallback(
    (next: PanelMode) => {
      if (next === mode) return;
      selectRoute(null);
      selectBarrier(null);
      writeLastPanelMode(next);
      navigate(PANEL_MODE_PATH[next]);
    },
    [mode, navigate, selectBarrier, selectRoute],
  );

  const forceShowRouteSelection = useCallback(() => {
    setSuppressedRouteId(null);
  }, []);
  const forceShowBarrierSelection = useCallback(() => {
    setSuppressedBarrierId(null);
  }, []);

  if (routeDraft !== null) {
    return (
      <Space direction="vertical" size={0} style={{ width: '100%' }}>
        <WorkspaceModalHeader
          title={t('routes.create.title')}
          onBack={cancelRouteDraft}
        />
        <RouteCreateForm />
      </Space>
    );
  }

  if (barrierDraft !== null) {
    return (
      <Space direction="vertical" size={0} style={{ width: '100%' }}>
        <WorkspaceModalHeader
          title={t('barriers.create.title')}
          onBack={cancelBarrierDraft}
        />
        {/*
          Auto-select the just-created barrier so the property sheet
          opens (with its waypoints list + map outline diamonds). Without
          this, the user lands back on the library and the configured
          waypoints would only appear after a manual click — exactly the
          UX gap that prompted the A+B work.
        */}
        <BarrierCreateForm onSaved={selectBarrier} />
      </Space>
    );
  }

  const showRouteProps =
    selectedRouteId !== null && suppressedRouteId !== selectedRouteId;
  const showBarrierProps =
    selectedBarrierId !== null && suppressedBarrierId !== selectedBarrierId;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <WorkspaceSegmented value={mode} onChange={handleSegmentChange} />
      {showRouteProps && selectedRouteId !== null ? (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <WorkspaceModalHeader
            title={t('routes.details.title', {
              id: _shortRouteId(selectedRouteId),
            })}
            onBack={() => setSuppressedRouteId(selectedRouteId)}
          />
          <RoutePropertiesPanel routeId={selectedRouteId} />
        </Space>
      ) : showBarrierProps && selectedBarrierId !== null ? (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <WorkspaceModalHeader
            title={t('barriers.details.title', { id: selectedBarrierId })}
            onBack={() => setSuppressedBarrierId(selectedBarrierId)}
          />
          <BarrierPropertiesPanel barrierId={selectedBarrierId} />
        </Space>
      ) : mode === 'routes' ? (
        <WorkspaceRoutesLibrary onRowReopen={forceShowRouteSelection} />
      ) : (
        <WorkspaceBarriersLibrary onRowReopen={forceShowBarrierSelection} />
      )}
    </Space>
  );
}

function _shortRouteId(routeId: string): string {
  // route_id is `sha256:<hex>` — show the first 8 hex chars.
  const colonIdx = routeId.indexOf(':');
  const tail = colonIdx >= 0 ? routeId.slice(colonIdx + 1) : routeId;
  return tail.slice(0, 8);
}
